import React, { useMemo } from 'react';
import { isDesktop } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  IMAGE_MODE,
  PAY_REDIRECT_ORIGIN,
  TRUCATE_STRING,
  CONTENT_TYPES as TYPES,
} from '../../../constants/contentTypes';
import { ERROR_CODE } from '../../../constants/errorCode';
import { NO_IMAGE_DEFAULT } from '../../../constants/noImage';
import useRouter from '../../../customHooks/useRouter';
import useWidgetTranslation from '../../../customHooks/useWidgetTranslation';
import {
  getSeoTitleInSection,
  validateAccessToContentByUrns,
  validateUrlRedirect,
} from '../../../utils/ContentUtils';
import { getContentImageByMode } from '../../../utils/imageUtils';
import { truncateString } from '../../../utils/objectUtils';
import {
  convertPath,
  replaceUrlParameter,
} from '../../../utils/pathRoutesUtils';
import {
  checkifHaveContentAccess,
  openStore,
} from '../../../utils/validationUserUtils';
import { PATH_ERROR } from '../../AppSectionRouter/constants';
import { selectCurrentContentPosition } from '../../ProfileManager/selectors';
import * as actions from './actions';
import { TITLES } from './constants';
import * as hooks from './hooks';
import * as selectors from './selectors';
// #region /////// COMPONENTS ///////
import {
  ContentOverviewFuture,
  ContentOverviewLive,
  ContentRelated,
  GenericModal,
  getStartEndDateForContent,
  SchedulePopover,
  unityContentsService,
} from '@tbx/experience-widgets-lib';
import { SectionErrorBoundary } from '../../../HOCs/WithErrorBoundary';
import Footer from '../../../components/Footer';
import SEO from '../../../components/Seo';
import SocialSharingList from '../../../components/SocialSharingList';
import useBrandInfo from '../../../customHooks/useBrandInfo';
import useClientConfigs from '../../../customHooks/useClientConfigs';
import { SINGULAR_EVENTS } from '../../../constants/singularEventTypes';
import { getSubscriberIdFromToken } from '../../../utils/jwtUtils';
import {
  isSingularEventEnabled,
  trackPaywallCtaClick,
} from '../../../utils/singularUtils';
import ExtendedFooter from '../../../components/ExtendedFooter';
import { useSingularPaywallView } from '../../../customHooks/useSingularPaywallView';
import { redirectToAccountInformation } from '../../Navbar/actions';
import { findImageSettingByType } from '../../../utils/appSettingUtils';
import { hasRedirectToStores } from '@tbx/utils/responsiveWebUtils';
// #endregion

// #region /////// VARIABLES goBackFunction ///////
const redirect = {
  url: '/',
  countGoBack: 0,
  boolGoBack: false,
};
// #endregion

function ContentOverViewSection({ accessToken, storeTime }) {
  const router = useRouter();
  const PATHS = useSelector((state) => selectors.selectPathList(state));
  const contentID = router.query.contentID;
  const mainDiv = React.useRef();
  const { t, i18n } = useTranslation();
  const translationTexts = useWidgetTranslation([...TITLES, 'at']);
  const [showFooter, setShowFooter] = React.useState(false);
  const dispatch = useDispatch();
  const BRAND_INFO = useBrandInfo();
  const {
    showContentDescription,
    useExtendedFooter,
    CustomFooter,
    showNetworksContent,
    hasDirectAccessToAddOns,
    validateHaveAccess = false,
    useSingular,
  } = useClientConfigs();

  const contentShareEnabled = isSingularEventEnabled(
    SINGULAR_EVENTS.CONTENT_SHARE,
    useSingular?.events,
  );

  const subscriberId = contentShareEnabled
    ? getSubscriberIdFromToken(accessToken?.access_token)
    : null;

  // const [contentRelatedId, setContentRelatedId] = React.useState(0);
  // const [contentEpisodeId, setContentEpisodeId] = React.useState(0);

  // #region /////// SELECTORS ///////
  const selectedContent = useSelector((state) =>
    selectors.selectContentWidget(state),
  );
  const selectedContentRelated = useSelector((state) =>
    selectors.selectContentRelatedWidget(state),
  );
  // const selectContentEpisodes = useSelector(state => selectors.selectContentEpisodesWidget(state));
  const authenticated = useSelector((state) =>
    selectors.selectAuthenticated(state),
  );
  const networks = useSelector((state) => selectors.selectNetworks(state));
  const redirectUrl = useSelector((state) =>
    selectors.selectContentsUrlByID(state),
  );
  const currentContentPosition = useSelector((state) =>
    selectCurrentContentPosition(state),
  );
  const selectUserUrns = useSelector((state) =>
    selectors.selectUserUrns(state),
  );
  const selectError = useSelector((state) => selectors.selectError(state));

  const appSettings = useSelector((state) =>
    selectors.selectAppSettings(state),
  );
  const contentAccess = useSelector((state) =>
    selectors.selectContentAccess(state),
  );
  const { mobileAppUrls } = appSettings;
  const showCustomMetaTags = useSelector((state) =>
    selectors.selectShowCustomMetaTags(state),
  );
  // #endregion

  // #region ///// HOOK EFFECTS /////
  hooks.useDidMount(
    accessToken,
    contentID,
    mainDiv,
    redirectUrl,
    authenticated,
  );
  const toastAdd = hooks.useToastAdd();
  hooks.useStoreTimeOnClickBack(storeTime, redirect, router, PATHS);

  React.useEffect(() => {
    if (validateHaveAccess) {
      dispatch(actions.getContentsAccess(accessToken, contentID));
    }
  }, [validateHaveAccess, accessToken, contentID, dispatch]);
  // #endregion

  const truncatedDescription = truncateString(
    selectedContent?.description,
    TRUCATE_STRING.METATAGS_DESCRIPTION,
  );
  const imageUrl = getContentImageByMode(selectedContent?.images, 'THUMB');
  const contentFlights = getStartEndDateForContent(selectedContent);
  const startDateContent = new Date(contentFlights?.start);
  const shouldShowBuySubscription = useMemo(() => {
    if (selectedContent?.free === true) {
      return true;
    }
    if (!authenticated) {
      return false;
    }

    if (validateHaveAccess) {
      return contentAccess.access;
    } else {
      return validateAccessToContentByUrns(
        selectedContent?.urn,
        selectUserUrns,
      );
    }
  }, [
    authenticated,
    selectedContent,
    selectUserUrns,
    contentAccess,
    validateHaveAccess,
  ]);

  const handleRedirectPay = async () => {
    try {
      await unityContentsService.getContentsUrlById(accessToken, contentID, {});
    } catch (error) {
      const apiError = error;
      if (apiError?.code === 'CS-011' && apiError?.paymentRedirectURL) {
        const link = replaceUrlParameter(
          apiError.paymentRedirectURL,
          'return_url',
          window.location.href,
        );
        window.location.href = link + `&origin=${PAY_REDIRECT_ORIGIN.CONTENT}`;
      } else {
        toastAdd({
          message: t('shared.errors.generic'),
          code: error.code || 'ERROR',
        });
        console.error('Error on onClickPaySubscription', error);
      }
    }
  };

  const redirectLogin = async () => {
    redirect.countGoBack = 0;
    const { origin } = window.location;
    const returnURL = origin + convertPath(PATHS.live, contentID);
    router.push(PATHS.login, { returnURL });
  };

  const onClickPaySubscription = async (contentId) => {
    trackPaywallCtaClick(
      {
        content_id: selectedContent?.id,
        title: selectedContent?.title,
        content_type: selectedContent?.contentType,
        urn: selectedContent?.urn,
        section_name: 'content_overview_live',
      },
      useSingular?.events,
    );

    if (!authenticated) {
      await redirectLogin();
    } else {
      if (validateHaveAccess) {
        await handleRedirectPay();
      } else {
        dispatch(
          redirectToAccountInformation(accessToken, hasDirectAccessToAddOns),
        );
      }
    }
  };

  const onClickPlayerButton = (contentId) => {
    const hasRedirect = validateUrlRedirect(currentContentPosition);
    hasRedirect &&
      authenticated &&
      dispatch(actions.getContentsUrlById(accessToken, contentId));

    if (hasRedirectToStores(isDesktop)) {
      openStore(mobileAppUrls);
    } else if (checkifHaveContentAccess(authenticated, selectedContent)) {
      if (!hasRedirect) {
        redirect.countGoBack = 0;
        router.push(convertPath(PATHS.player, contentID));
      }
    } else {
      redirect.countGoBack = 0;
      const { origin } = window.location;
      // const returnURL = hasRedirect ? origin + '/content/' + contentID : origin + `/content/${ currentContentPosition ? currentContentPosition?.currentContent.id : contentId}`;
      const returnURL = origin + convertPath(PATHS.live, contentID);
      router.push(PATHS.login, { returnURL });
    }
  };

  const onClickContentSelected = React.useCallback(
    async (ev, contentId) => {
      ev.preventDefault();
      setShowFooter(false);
      redirect.countGoBack = 0;
      router.push(convertPath(PATHS.live, contentId));
    },
    [router],
  );

  const goBackFunction = React.useCallback(async () => {
    if (
      (redirect.countGoBack >= 2 && redirect.boolGoBack) ||
      router.location.state?.oneBack.path === PATHS.player ||
      !router.location.state?.oneBack.path
    ) {
      redirect.countGoBack = 0;
      return router.push(redirect.url);
    }
    redirect.countGoBack = redirect.countGoBack + 1;
    return router.goBack();
  }, [router]);

  const catchError = (error) => {
    return router.push(PATH_ERROR, { error });
  };

  const renderFooter = () => {
    if (!showFooter) {
      return <></>;
    }
    if (CustomFooter) {
      return <CustomFooter mobileAppUrls={mobileAppUrls} />;
    }
    if (useExtendedFooter) {
      return <ExtendedFooter />;
    }
    return <Footer />;
  };

  const contentNoImageProps = (imageMode) => {
    const { w, h } = findImageSettingByType(appSettings.imageSizes, imageMode);
    return {
      width: w || NO_IMAGE_DEFAULT(imageMode).width,
      height: h || NO_IMAGE_DEFAULT(imageMode).height,
      backgroundColorOne: NO_IMAGE_DEFAULT().backgroundColorOne,
      backgroundColorTwo: NO_IMAGE_DEFAULT().backgroundColorTwo,
    };
  };

  React.useEffect(() => {
    if (
      ERROR_CODE[selectError.code] ||
      (!!selectError.code && !!selectError.details && !!selectError.message)
    ) {
      toastAdd(selectError);
      return goBackFunction();
    }
  }, [selectError]);

  const initialContentAccessRef = React.useRef(contentAccess);
  const isAccessResolved =
    !validateHaveAccess ||
    !authenticated ||
    contentAccess !== initialContentAccessRef.current;

  useSingularPaywallView(
    authenticated && Boolean(
      selectError?.code === ERROR_CODE['CS-011'] ||
      (selectedContent?.id && isAccessResolved && !shouldShowBuySubscription),
    ),
    useSingular?.events,
    {
      content_id: selectedContent?.id,
      title: selectedContent?.title,
      content_type: selectedContent?.contentType,
      urn: selectedContent?.urn,
      section_name: 'content_overview_live',
    },
  );

  return (
    <React.Fragment>
      {!showCustomMetaTags && selectedContent && (
        <SEO
          title={getSeoTitleInSection(
            selectedContent,
            t,
            i18n.language,
            '',
            BRAND_INFO,
          )}
          contentTitle={getSeoTitleInSection(
            selectedContent,
            t,
            i18n.language,
            '',
            BRAND_INFO,
          )}
          description={truncatedDescription}
          image={imageUrl?.url}
        />
      )}

      <div ref={mainDiv} className="main-content">
        <div
          // eslint-disable-next-line react/no-unknown-property
          onLoad={() => setShowFooter(true)}
          className="ContentOverviewLiveSection"
        >
          {selectedContent?.live &&
          selectedContent?.contentType === TYPES.SPORT ? (
            <ContentOverviewFuture
              content={selectedContent}
              isLoading={!selectedContent}
              onClickBackButton={goBackFunction}
              onClickPlayerButton={onClickPlayerButton}
              translationTexts={translationTexts}
              showPlayIcon={false}
              backgroundImage={'/images/bg_ficha_sport.jpg'}
              isModal={false}
              placementTooltip="right"
              showRating={!!showContentDescription}
              showChannel={showNetworksContent}
              showYear={!!showContentDescription}
              showDuration={!!showContentDescription}
              showLive={false}
              showScheduleButton={false}
              hasAccessTo={shouldShowBuySubscription}
              onClickPaySubscription={onClickPaySubscription}
              lang={i18n.language}
            >
              <>
                <GenericModal
                  title={translationTexts.share_on}
                  // eslint-disable-next-line react/no-children-prop
                  children={
                    <SocialSharingList
                      content={selectedContent}
                      trackingEnabled={contentShareEnabled}
                      subscriberId={subscriberId}
                    />
                  }
                  isModal={false}
                  placement="right"
                />
                {startDateContent > new Date() && (
                  <SchedulePopover
                    content={selectedContent}
                    schedule=""
                    buttonType="icon"
                    placement="right"
                  />
                )}
              </>
            </ContentOverviewFuture>
          ) : (
            <ContentOverviewLive
              translationTexts={translationTexts}
              content={selectedContent}
              onClickPlayerButton={onClickPlayerButton}
              onClickBackButton={goBackFunction}
              networks={networks}
              isLoading={!selectedContent}
              currentContentPosition={currentContentPosition}
              contentNoImageProps={{
                backgroundColorOne: NO_IMAGE_DEFAULT().backgroundColorOne,
                backgroundColorTwo: NO_IMAGE_DEFAULT().backgroundColorTwo,
              }}
              catchError={catchError}
              redirectContent={validateUrlRedirect(currentContentPosition)}
              showPlayIcon={false}
              hasAccessTo={shouldShowBuySubscription}
              onClickPaySubscription={onClickPaySubscription}
            />
          )}
        </div>
        {/* {selectedContent && selectedContent.statsUrl && (
          <IFrameComponent source={`${selectedContent.statsUrl}`} />
        )} */}

        <div>
          {selectedContentRelated &&
            selectedContentRelated.result &&
            Array.isArray(selectedContentRelated.result) &&
            selectedContentRelated.result.length > 0 && (
              <ContentRelated
                content={selectedContentRelated?.result}
                translationTexts={translationTexts}
                imageMode={IMAGE_MODE.THUMB}
                contentNoImageProps={contentNoImageProps(IMAGE_MODE.THUMB)}
                onClickContentSelected={onClickContentSelected}
                isLoading={!selectedContentRelated}
                catchError={() => {}}
                textMode={'outer'}
              />
            )}
        </div>
        {renderFooter()}
      </div>
    </React.Fragment>
  );
}

export default SectionErrorBoundary(ContentOverViewSection);
